﻿import { useEffect, useMemo, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import {
  Activity,
  Box,
  Database,
  Download,
  Grid,
  LoaderCircle,
  PieChart,
  RefreshCw,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import {
  type StatisticsDashboard,
  type StatisticsHealthDistributionItem,
  type StatisticsRange,
  type StatisticsTableRow,
  type StatisticsTokenDistributionItem,
  type StatisticsTrendPoint,
  type SwitchMode,
} from '../../shared/types';
import { OAUTH_PROVIDER_ICON_SRC } from '../lib/oauth-provider-icon-src';
import './statistics-center.css';

interface StatisticsCenterProps {
  dashboard: StatisticsDashboard | null;
  activeRange: StatisticsRange;
  mode: SwitchMode;
  profileMetaById: Record<
    string,
    {
      accountLabel: string;
      accountEmail: string | null;
    }
  >;
  contextMeta: {
    softwareAccount: string;
    nickname: string;
    currentDevice: string;
    deviceIdentity: string;
  };
  refreshSource?: 'background' | 'range' | 'mode' | 'manual' | null;
  loading: boolean;
  errorMessage: string | null;
  onModeChange: (mode: SwitchMode) => void;
  onRangeChange: (range: StatisticsRange) => void;
  onRefresh: () => void;
  onClose: () => void;
}

type BreakdownItem = StatisticsHealthDistributionItem | StatisticsTokenDistributionItem;

interface TrendCoordinate {
  point: StatisticsTrendPoint;
  x: number;
  primaryY: number;
  secondaryY: number;
}

interface TrendInteractionPoint extends TrendCoordinate {}

interface TrendTick {
  label: string;
  x: number;
  row: number;
  anchor: 'start' | 'middle' | 'end';
}

interface TrendGuide {
  label: string;
  y: number;
  value: number;
}

type TableSortKey = 'name' | 'planType' | 'status' | 'usedTokens' | 'remainingTokens' | 'usagePercent' | 'lastSyncAt';
type TableSortDirection = 'asc' | 'desc';

interface TableSortState {
  key: TableSortKey;
  direction: TableSortDirection;
}

const RANGE_LABELS: Record<StatisticsRange, string> = {
  today: '今日',
  '7d': '7天',
  '30d': '30天',
};

const DONUT_TONE_COLORS = {
  healthy: '#45dd82',
  warning: '#ffc640',
  danger: '#ff5757',
  offline: '#ff5757',
  input: '#4f7bff',
  output: '#9d52ff',
} as const;

const TABLE_TEXT_COLLATOR = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' });
const PLAN_TYPE_SORT_PRIORITY = ['Pro 20x', 'Pro 5x', 'Plus', 'Team', 'Business', 'Free', 'Unknown'] as const;
const PLAN_TYPE_SORT_ORDER = new Map(PLAN_TYPE_SORT_PRIORITY.map((planType, index) => [planType, index]));
const STATUS_TONE_SORT_ORDER = {
  healthy: 0,
  warning: 1,
  danger: 2,
  offline: 3,
} as const;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const formatSignedDelta = (value: number): string => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

const formatLargeInteger = (value: number): string => new Intl.NumberFormat('en-US').format(Math.round(value));

const formatCompactValue = (value: number): string => {
  const absoluteValue = Math.abs(value);

  if (absoluteValue >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }

  if (absoluteValue >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }

  if (absoluteValue >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return `${Math.round(value)}`;
};

const formatPercent = (value: number): string => `${Math.round(value)}%`;

const formatSharePercent = (value: number): string => `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`;

const getInitialTableSortDirection = (key: TableSortKey): TableSortDirection =>
  key === 'name' || key === 'planType' || key === 'status' ? 'asc' : 'desc';

const compareStatisticsTableRows = (left: StatisticsTableRow, right: StatisticsTableRow, sort: TableSortState): number => {
  switch (sort.key) {
    case 'name':
      return TABLE_TEXT_COLLATOR.compare(left.name, right.name);
    case 'planType':
      return (
        (PLAN_TYPE_SORT_ORDER.get(left.planType) ?? Number.MAX_SAFE_INTEGER) -
        (PLAN_TYPE_SORT_ORDER.get(right.planType) ?? Number.MAX_SAFE_INTEGER)
      );
    case 'status': {
      const toneDelta =
        (STATUS_TONE_SORT_ORDER[left.statusTone] ?? Number.MAX_SAFE_INTEGER) -
        (STATUS_TONE_SORT_ORDER[right.statusTone] ?? Number.MAX_SAFE_INTEGER);
      return toneDelta !== 0 ? toneDelta : TABLE_TEXT_COLLATOR.compare(left.statusLabel, right.statusLabel);
    }
    case 'usedTokens':
      return left.usedTokens - right.usedTokens;
    case 'remainingTokens':
      return left.remainingTokens - right.remainingTokens;
    case 'usagePercent':
      return left.usagePercent - right.usagePercent;
    case 'lastSyncAt': {
      const leftTime = left.lastSyncAt ? Date.parse(left.lastSyncAt) : Number.NaN;
      const rightTime = right.lastSyncAt ? Date.parse(right.lastSyncAt) : Number.NaN;
      const leftValid = Number.isFinite(leftTime);
      const rightValid = Number.isFinite(rightTime);

      if (!leftValid && !rightValid) {
        return 0;
      }

      if (!leftValid) {
        return 1;
      }

      if (!rightValid) {
        return -1;
      }

      return leftTime - rightTime;
    }
  }
};

const formatDateTimeLabel = (value: string): string => {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const escapeHtml = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const getTrendRangeBounds = (range: StatisticsRange, generatedAt: string): { startMs: number; endMs: number } => {
  const endDate = new Date(generatedAt);
  if (!Number.isFinite(endDate.getTime())) {
    const fallbackEnd = Date.now();
    const fallbackStart = new Date(fallbackEnd);
    fallbackStart.setHours(0, 0, 0, 0);
    return {
      startMs: fallbackStart.getTime(),
      endMs: fallbackEnd,
    };
  }

  const startDate = new Date(endDate);
  startDate.setHours(0, 0, 0, 0);

  if (range === '7d') {
    startDate.setDate(startDate.getDate() - 6);
  } else if (range === '30d') {
    startDate.setDate(startDate.getDate() - 29);
  }

  return {
    startMs: startDate.getTime(),
    endMs: Math.max(startDate.getTime() + 1, endDate.getTime()),
  };
};

const getTrendAxisDateLabel = (date: Date): string =>
  date.toLocaleDateString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
  });

const formatTrendPointLabel = (date: Date, range: StatisticsRange): string =>
  range === 'today'
    ? date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : getTrendAxisDateLabel(date);

const formatTrendPointShortLabel = (date: Date, range: StatisticsRange): string =>
  range === 'today'
    ? date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : getTrendAxisDateLabel(date);

const buildTrendInteractionBucketTimes = (range: StatisticsRange, generatedAt: string): number[] => {
  const { startMs, endMs } = getTrendRangeBounds(range, generatedAt);
  const generatedDate = new Date(generatedAt);
  const safeGeneratedDate = Number.isFinite(generatedDate.getTime()) ? generatedDate : new Date();
  const dayStart = new Date(startMs);

  if (range === 'today') {
    const bucketTimes = Array.from({ length: safeGeneratedDate.getHours() + 1 }, (_, index) => {
      const pointTime = new Date(dayStart);
      pointTime.setHours(index, 0, 0, 0);
      return pointTime.getTime();
    });

    const lastHourBucketTime = bucketTimes[bucketTimes.length - 1] ?? startMs;
    if (endMs - lastHourBucketTime > 60_000) {
      bucketTimes.push(endMs);
    }

    return bucketTimes;
  }

  const bucketCount = range === '7d' ? 7 : 30;
  return Array.from({ length: bucketCount }, (_, index) => {
    if (index === bucketCount - 1) {
      return endMs;
    }

    const pointTime = new Date(dayStart);
    pointTime.setDate(dayStart.getDate() + index);
    pointTime.setHours(23, 59, 59, 999);
    return pointTime.getTime();
  });
};

const buildBackfilledPreviewPoints = ({
  range,
  generatedAt,
  latestPoint,
}: {
  range: StatisticsRange;
  generatedAt: string;
  latestPoint: StatisticsTrendPoint;
}): StatisticsTrendPoint[] => {
  const bucketTimes = buildTrendInteractionBucketTimes(range, generatedAt);

  return bucketTimes.map((bucketTime, index) => {
    const ratio = bucketTimes.length <= 1 ? 1 : index / (bucketTimes.length - 1);
    const pointTime = new Date(bucketTime);
    const waveDecay = 1 - ratio;
    const usedScale =
      index === bucketTimes.length - 1
        ? 1
        : clamp(0.9 + ratio * 0.1 + Math.sin(ratio * Math.PI * 3.1) * 0.028 * waveDecay, 0.87, 1.02);
    const averageScale =
      index === bucketTimes.length - 1
        ? 1
        : clamp(0.885 + ratio * 0.115 + Math.cos(ratio * Math.PI * 2.4 + 0.35) * 0.016 * waveDecay, 0.87, 1.01);
    const usedTokens =
      index === bucketTimes.length - 1 ? latestPoint.usedTokens : Math.max(1, Math.round(latestPoint.usedTokens * usedScale));
    const averageTokens =
      index === bucketTimes.length - 1
        ? latestPoint.averageTokens
        : Math.max(1, Math.round(latestPoint.averageTokens * averageScale));

    return {
      label: formatTrendPointLabel(pointTime, range),
      shortLabel: formatTrendPointShortLabel(pointTime, range),
      timestamp: pointTime.toISOString(),
      usedTokens,
      averageTokens,
    };
  });
};

const buildTrendAxisTicks = (
  range: StatisticsRange,
  generatedAt: string,
  width: number,
): TrendTick[] => {
  const { startMs, endMs } = getTrendRangeBounds(range, generatedAt);
  const rangeWidthMs = Math.max(1, endMs - startMs);
  const generatedDate = new Date(generatedAt);
  const safeGeneratedDate = Number.isFinite(generatedDate.getTime()) ? generatedDate : new Date();

  if (range === 'today') {
    const tickTimes: number[] = [];
    for (let tickTime = startMs; tickTime <= endMs; tickTime += 3 * 60 * 60_000) {
      tickTimes.push(tickTime);
    }

    const lastTickTime = tickTimes[tickTimes.length - 1] ?? startMs;
    if (endMs - lastTickTime < 90 * 60 * 1000) {
      tickTimes[tickTimes.length - 1] = endMs;
    } else if (lastTickTime !== endMs) {
      tickTimes.push(endMs);
    }

    return tickTimes.map((tickTime, index, allTicks) => {
      const tickDate = new Date(tickTime);
      const isLatestTick = index === allTicks.length - 1;
      const isDenseLayout = allTicks.length > 12;
      const isAlignedHour =
        tickDate.getMinutes() === 0 && tickDate.getSeconds() === 0 && tickDate.getMilliseconds() === 0;
      return {
        label:
          isLatestTick && !isAlignedHour
            ? tickDate.toLocaleTimeString('zh-CN', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })
            : `${String(tickDate.getHours()).padStart(2, '0')}:00`,
        x: clamp(((tickTime - startMs) / rangeWidthMs) * width, 0, width),
        row: isDenseLayout ? index % 2 : 0,
        anchor: index === 0 ? 'start' : isLatestTick ? 'end' : 'middle',
      };
    });
  }

  const tickCount = range === '7d' ? 7 : 10;
  const startDate = new Date(safeGeneratedDate);
  startDate.setHours(0, 0, 0, 0);
  startDate.setDate(startDate.getDate() - (range === '7d' ? 6 : 29));

  return Array.from({ length: tickCount }, (_, index) => {
    const tickDate =
      range === '7d'
        ? new Date(startDate.getTime() + index * 24 * 60 * 60_000)
        : new Date(startMs + (rangeWidthMs * index) / Math.max(1, tickCount - 1));
    const isDenseLayout = tickCount > 12;
    return {
      label: getTrendAxisDateLabel(tickDate),
      x:
        range === '7d'
          ? (index / Math.max(1, tickCount - 1)) * width
          : clamp(((tickDate.getTime() - startMs) / rangeWidthMs) * width, 0, width),
      row: isDenseLayout ? index % 2 : 0,
      anchor: index === 0 ? 'start' : index === tickCount - 1 ? 'end' : 'middle',
    };
  });
};

const buildSmoothPath = (
  coordinates: TrendCoordinate[],
  key: 'primaryY' | 'secondaryY',
): string => {
  if (coordinates.length === 0) {
    return '';
  }

  if (coordinates.length === 1) {
    const point = coordinates[0];
    return `M ${point.x.toFixed(2)} ${point[key].toFixed(2)}`;
  }

  if (coordinates.length === 2) {
    return `M ${coordinates[0].x.toFixed(2)} ${coordinates[0][key].toFixed(2)} L ${coordinates[1].x.toFixed(2)} ${coordinates[1][key].toFixed(2)}`;
  }

  const slopes: number[] = [];
  const tangents: number[] = Array.from({ length: coordinates.length }, () => 0);

  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const current = coordinates[index];
    const next = coordinates[index + 1];
    const deltaX = next.x - current.x;
    slopes.push(deltaX > 0 ? (next[key] - current[key]) / deltaX : 0);
  }

  tangents[0] = slopes[0] ?? 0;
  tangents[coordinates.length - 1] = slopes[slopes.length - 1] ?? 0;

  for (let index = 1; index < coordinates.length - 1; index += 1) {
    const previousSlope = slopes[index - 1] ?? 0;
    const nextSlope = slopes[index] ?? 0;
    tangents[index] =
      previousSlope === 0 || nextSlope === 0 || previousSlope * nextSlope < 0
        ? 0
        : (previousSlope + nextSlope) / 2;
  }

  for (let index = 0; index < slopes.length; index += 1) {
    const slope = slopes[index] ?? 0;
    if (slope === 0) {
      tangents[index] = 0;
      tangents[index + 1] = 0;
      continue;
    }

    const alpha = tangents[index] / slope;
    const beta = tangents[index + 1] / slope;
    const magnitude = alpha * alpha + beta * beta;

    if (magnitude > 9) {
      const scale = 3 / Math.sqrt(magnitude);
      tangents[index] = scale * alpha * slope;
      tangents[index + 1] = scale * beta * slope;
    }
  }

  let path = `M ${coordinates[0].x.toFixed(2)} ${coordinates[0][key].toFixed(2)}`;

  for (let index = 1; index < coordinates.length; index += 1) {
    const previous = coordinates[index - 1];
    const current = coordinates[index];
    const deltaX = current.x - previous.x;
    if (deltaX <= 0) {
      path += ` L ${current.x.toFixed(2)} ${current[key].toFixed(2)}`;
      continue;
    }

    const controlPointOneX = previous.x + deltaX / 3;
    const controlPointTwoX = current.x - deltaX / 3;
    const controlPointOneY = previous[key] + (tangents[index - 1] * deltaX) / 3;
    const controlPointTwoY = current[key] - (tangents[index] * deltaX) / 3;

    path += ` C ${controlPointOneX.toFixed(2)} ${controlPointOneY.toFixed(2)}, ${controlPointTwoX.toFixed(2)} ${controlPointTwoY.toFixed(2)}, ${current.x.toFixed(2)} ${current[key].toFixed(2)}`;
  }

  return path;
};

const buildAreaPath = (
  coordinates: TrendCoordinate[],
  baselineY: number,
): string => {
  if (coordinates.length === 0) {
    return '';
  }

  if (coordinates.length === 1) {
    const point = coordinates[0];
    return `M ${point.x.toFixed(2)} ${baselineY.toFixed(2)} L ${point.x.toFixed(2)} ${point.primaryY.toFixed(2)} L ${point.x.toFixed(2)} ${baselineY.toFixed(2)} Z`;
  }

  const linePath = buildSmoothPath(coordinates, 'primaryY');
  const firstPoint = coordinates[0];
  const lastPoint = coordinates[coordinates.length - 1];
  return `${linePath} L ${lastPoint.x.toFixed(2)} ${baselineY.toFixed(2)} L ${firstPoint.x.toFixed(2)} ${baselineY.toFixed(2)} Z`;
};

const buildDonutGradient = (items: BreakdownItem[], total: number): string => {
  if (total <= 0) {
    return 'conic-gradient(from -90deg, rgba(41, 59, 102, 0.48) 0deg 360deg)';
  }

  let consumedDegrees = 0;
  const segments = items
    .map((item) => {
      const itemValue = 'value' in item ? item.value : item.count;
      if (itemValue <= 0) {
        return null;
      }

      const sweep = (itemValue / total) * 360;
      const start = consumedDegrees;
      consumedDegrees += sweep;
      const toneColor = DONUT_TONE_COLORS[item.tone];
      return `${toneColor} ${start.toFixed(2)}deg ${consumedDegrees.toFixed(2)}deg`;
    })
    .filter((segment): segment is string => Boolean(segment));

  return segments.length > 0
    ? `conic-gradient(from -90deg, ${segments.join(', ')})`
    : 'conic-gradient(from -90deg, rgba(41, 59, 102, 0.48) 0deg 360deg)';
};

const getProfileAccountLabel = (
  profileMetaById: StatisticsCenterProps['profileMetaById'],
  profileId: string,
): string => profileMetaById[profileId]?.accountLabel?.trim() || '未填写备注邮箱';

const getProfileAccountId = (
  profileMetaById: StatisticsCenterProps['profileMetaById'],
  profileId: string,
): string => profileMetaById[profileId]?.accountEmail?.trim() || getProfileAccountLabel(profileMetaById, profileId);

const buildStatisticsExportDocument = (
  dashboard: StatisticsDashboard,
  mode: SwitchMode,
  profileMetaById: StatisticsCenterProps['profileMetaById'],
  contextMeta: StatisticsCenterProps['contextMeta'],
): string => {
  const modeLabel = mode === 'openclaw' ? 'OpenClaw' : 'Codex';
  const overviewRows = [
    ['总空间数', String(dashboard.totalSpaces), `${dashboard.compareLabel} ${formatSignedDelta(dashboard.totalSpacesDeltaPercent)}`],
    ['可用空间', String(dashboard.availableSpaces), `${dashboard.compareLabel} ${formatSignedDelta(dashboard.availableSpacesDeltaPercent)}`],
    ['周消耗', formatLargeInteger(dashboard.estimatedUsedTokens), `${dashboard.compareLabel} ${formatSignedDelta(dashboard.usedTokensDeltaPercent)}`],
    ['估算剩余', formatLargeInteger(dashboard.estimatedRemainingTokens), `${dashboard.compareLabel} ${formatSignedDelta(dashboard.remainingTokensDeltaPercent)}`],
    ['健康度', formatPercent(dashboard.averageHealthPercent), `${dashboard.compareLabel} ${formatSignedDelta(dashboard.averageHealthDeltaPercent)}`],
    ['估算总容量', formatCompactValue(dashboard.estimatedTotalTokens), `${dashboard.compareLabel} ${formatSignedDelta(dashboard.totalTokensDeltaPercent)}`],
  ]
    .map(
      ([metric, value, delta]) => `
        <tr>
          <td>${escapeHtml(metric)}</td>
          <td class="value">${escapeHtml(value)}</td>
          <td>${escapeHtml(delta)}</td>
        </tr>`,
    )
    .join('');

  const contextRows = [
    ['软件账号', contextMeta.softwareAccount],
    ['软件昵称', contextMeta.nickname],
    ['当前设备', contextMeta.currentDevice],
    ['设备标识', contextMeta.deviceIdentity],
  ]
    .map(
      ([label, value]) => `
        <tr>
          <td>${escapeHtml(label)}</td>
          <td class="text-left">${escapeHtml(value)}</td>
        </tr>`,
    )
    .join('');

  const trendRows = dashboard.trendPoints
    .slice()
    .sort((left, right) => {
      const leftTime = Date.parse(left.timestamp);
      const rightTime = Date.parse(right.timestamp);

      if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
        return leftTime - rightTime;
      }

      return left.label.localeCompare(right.label, 'zh-CN');
    })
    .map(
      (point) => `
        <tr>
          <td>${escapeHtml(point.label)}</td>
          <td>${escapeHtml(formatLargeInteger(point.usedTokens))}</td>
          <td>${escapeHtml(formatLargeInteger(point.averageTokens))}</td>
        </tr>`,
    )
    .join('');

  const rankingRows = dashboard.ranking
    .map(
      (item) => `
        <tr>
          <td>${escapeHtml(item.name)}</td>
          <td>${escapeHtml(getProfileAccountLabel(profileMetaById, item.profileId))}</td>
          <td>${escapeHtml(formatLargeInteger(item.usedTokens))}</td>
          <td>${escapeHtml(formatPercent(item.usagePercent))}</td>
          <td>${escapeHtml(formatSharePercent(item.sharePercent))}</td>
        </tr>`,
    )
    .join('');

  const healthRows = dashboard.healthDistribution
    .map(
      (item) => `
        <tr>
          <td>${escapeHtml(item.label)}</td>
          <td>${escapeHtml(String(item.count))}</td>
          <td>${escapeHtml(formatSharePercent(item.sharePercent))}</td>
        </tr>`,
    )
    .join('');

  const tokenRows = dashboard.tokenDistribution
    .map(
      (item) => `
        <tr>
          <td>${escapeHtml(item.label)}</td>
          <td>${escapeHtml(formatCompactValue(item.value))}</td>
          <td>${escapeHtml(formatSharePercent(item.sharePercent))}</td>
        </tr>`,
    )
    .join('');

  const tableRows = dashboard.tableRows
    .map(
      (row) => `
        <tr>
          <td>${escapeHtml(row.name)}</td>
          <td>${escapeHtml(getProfileAccountLabel(profileMetaById, row.profileId))}</td>
          <td class="pill pill-${row.planType.toLowerCase()}">${escapeHtml(row.planType)}</td>
          <td class="status status-${row.statusTone}">${escapeHtml(row.statusLabel)}</td>
          <td>${escapeHtml(formatLargeInteger(row.usedTokens))}</td>
          <td>${escapeHtml(formatLargeInteger(row.remainingTokens))}</td>
          <td>${escapeHtml(formatPercent(row.usagePercent))}</td>
          <td>${escapeHtml(row.lastSyncAt ? formatDateTimeLabel(row.lastSyncAt) : '--')}</td>
          <td>${escapeHtml(row.actionLabel)}</td>
        </tr>`,
    )
    .join('');

  const historyNote = dashboard.historyBackfilled
    ? '趋势点优先来自本地已采样历史快照；当前 Token 总量和输入输出占比仍按已探测额度百分比换算。'
    : '趋势点来自本地历史快照；当前 Token 总量和输入输出占比仍按已探测额度百分比换算。';

  return `<!DOCTYPE html>
<html lang="zh-CN">
  <head>
    <meta charset="utf-8" />
    <meta http-equiv="Content-Type" content="application/vnd.ms-excel; charset=UTF-8" />
    <title>${modeLabel}统计中心导出</title>
    <style>
      body { margin: 18px; color: #10213f; font: 12px/1.5 "Microsoft YaHei", "PingFang SC", Arial, sans-serif; background: #f4f7ff; }
      h1 { margin: 0 0 8px; color: #08142b; font-size: 24px; }
      h2 { margin: 0 0 10px; color: #10275d; font-size: 16px; }
      .meta { margin: 0 0 14px; color: #4b5c7c; }
      .value { color: #0f2f75; font-weight: 800; }
      .note { margin: 8px 0 0; color: #5b6c90; }
      .layout { width: 100%; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
      .layout > tbody > tr > td { vertical-align: top; }
      .layout .section-cell { width: calc((100% - 20px) / 2); }
      .gap { width: 20px; }
      .spacer { height: 16px; }
      .section-cell { padding: 0; }
      .sheet-table { width: 100%; border-collapse: collapse; table-layout: auto; background: #ffffff; border: 1px solid #d6e0f4; }
      .sheet-table th, .sheet-table td { min-width: 86px; padding: 8px 10px; border: 1px solid #d6e0f4; vertical-align: middle; text-align: center; white-space: normal; word-break: break-word; }
      .sheet-table th { background: #eaf1ff; color: #153166; font-weight: 700; }
      .text-left { text-align: left; }
      .pill { font-weight: 700; text-align: center; }
      .pill-plus, .pill-pro { background: rgba(44, 32, 18, 0.92); color: #f8c66d; }
      .pill-team, .pill-business { background: rgba(74, 44, 145, 0.92); color: #e9ddff; }
      .pill-free, .pill-unknown { background: #55657f; color: #edf4ff; }
      .status { font-weight: 700; }
      .status-healthy { color: #1f9f5f; }
      .status-warning { color: #c98a07; }
      .status-danger, .status-offline { color: #d9485f; }
      .section-stack { display: grid; gap: 14px; }
    </style>
  </head>
  <body>
    <h1>${modeLabel}统计中心导出</h1>
    <p class="meta">导出时间：${escapeHtml(formatDateTimeLabel(dashboard.generatedAt))} ｜ 时间范围：${escapeHtml(RANGE_LABELS[dashboard.range])}</p>
    <p class="note">${escapeHtml(historyNote)}</p>
    <table class="layout">
      <tbody>
        <tr>
          <td class="section-cell">
            <h2>概览指标</h2>
            <table class="sheet-table">
              <thead>
                <tr>
                  <th>指标</th>
                  <th>当前数值</th>
                  <th>对比</th>
                </tr>
              </thead>
              <tbody>${overviewRows}</tbody>
            </table>
          </td>
          <td class="gap"></td>
          <td class="section-cell">
            <h2>账号与设备</h2>
            <table class="sheet-table">
              <thead>
                <tr>
                  <th>字段</th>
                  <th>当前信息</th>
                </tr>
              </thead>
              <tbody>${contextRows}</tbody>
            </table>
          </td>
        </tr>
        <tr><td class="spacer" colspan="3"></td></tr>
        <tr>
          <td class="section-cell">
            <h2>额度消耗趋势</h2>
            <table class="sheet-table">
              <thead>
                <tr>
                  <th>时间点</th>
                  <th>消耗 Token</th>
                  <th>7日均值</th>
                </tr>
              </thead>
              <tbody>${trendRows}</tbody>
            </table>
          </td>
          <td class="gap"></td>
          <td class="section-cell">
            <div class="section-stack">
              <div>
                <h2>周消耗排行</h2>
                <table class="sheet-table">
                  <thead>
                    <tr>
                      <th>空间名称</th>
                      <th>备注邮箱 / 账号</th>
                      <th>已用 Token</th>
                      <th>使用率</th>
                      <th>占比</th>
                    </tr>
                  </thead>
                  <tbody>${rankingRows}</tbody>
                </table>
              </div>
              <div>
                <h2>空间状态分布</h2>
                <table class="sheet-table">
                  <thead>
                    <tr>
                      <th>状态区间</th>
                      <th>空间数</th>
                      <th>占比</th>
                    </tr>
                  </thead>
                  <tbody>${healthRows}</tbody>
                </table>
              </div>
              <div>
                <h2>Token 估算 (周)</h2>
                <table class="sheet-table">
                  <thead>
                    <tr>
                      <th>类型</th>
                      <th>数值</th>
                      <th>占比</th>
                    </tr>
                  </thead>
                  <tbody>${tokenRows}</tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>
        <tr><td class="spacer" colspan="3"></td></tr>
        <tr>
          <td class="section-cell" colspan="3">
            <h2>空间使用明细</h2>
            <table class="sheet-table">
              <thead>
                <tr>
                  <th>空间名称</th>
                  <th>备注邮箱 / 账号</th>
                  <th>类型</th>
                  <th>状态</th>
                  <th>本周已用 (Token)</th>
                  <th>估算剩余 (Token)</th>
                  <th>使用率</th>
                  <th>最后同步</th>
                  <th>建议操作</th>
                </tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  </body>
</html>`;
};

const createPlaceholderDashboard = (range: StatisticsRange): StatisticsDashboard => ({
  range,
  generatedAt: new Date().toISOString(),
  compareLabel: range === 'today' ? '较昨日' : range === '7d' ? '较7天前' : '较30天前', 
  historyBackfilled: false,
  totalSpaces: 0,
  availableSpaces: 0,
  activeSpaces: 0,
  disabledSpaces: 0,
  healthySpaces: 0,
  warningSpaces: 0,
  abnormalSpaces: 0,
  averageHealthPercent: 0,
  estimatedUsedTokens: 0,
  estimatedRemainingTokens: 0,
  estimatedTotalTokens: 0,
  estimatedInputTokens: 0,
  estimatedOutputTokens: 0,
  totalSpacesDeltaPercent: 0,
  availableSpacesDeltaPercent: 0,
  usedTokensDeltaPercent: 0,
  remainingTokensDeltaPercent: 0,
  averageHealthDeltaPercent: 0,
  totalTokensDeltaPercent: 0,
  trendPoints: [],
  ranking: [],
  healthDistribution: [],
  tokenDistribution: [],
  tableRows: [],
});

function StatisticsMetricIcon({
  kind,
}: {
  kind: 'spaces' | 'available' | 'used' | 'remaining' | 'health' | 'token';
}) {
  switch (kind) {
    case 'spaces':
      return <Box aria-hidden="true" />;
    case 'available':
      return <Database aria-hidden="true" />;
    case 'used':
      return <Zap aria-hidden="true" />;
    case 'remaining':
      return <PieChart aria-hidden="true" />;
    case 'health':
      return <ShieldCheck aria-hidden="true" />;
    case 'token':
      return <Activity aria-hidden="true" />;
  }
}

const CircularBreakdownChart = ({
  total,
  items,
  centerLabel,
  centerValue,
  tone,
}: {
  total: number;
  items: BreakdownItem[];
  centerLabel: string;
  centerValue: string;
  tone: 'health' | 'token';
}) => {
  const gradient = useMemo(() => buildDonutGradient(items, total), [items, total]);
  const style = { '--statistics-donut-gradient': gradient } as CSSProperties;

  return (
    <div className={`statistics-donut statistics-donut--${tone}`} style={style}>
      <div className="statistics-donut__ring" aria-hidden="true" />
      <div className="statistics-donut__center">
        <strong>{centerValue}</strong>
        <span>{centerLabel}</span>
      </div>
    </div>
  );
};

export function StatisticsCenter({
  dashboard,
  activeRange,
  mode,
  profileMetaById,
  contextMeta,
  refreshSource = null,
  loading,
  errorMessage,
  onModeChange,
  onRangeChange,
  onRefresh,
  onClose,
}: StatisticsCenterProps) {
  const safeDashboard = dashboard ?? createPlaceholderDashboard(activeRange);
  const showLoadingStage = loading && !dashboard;
  const showEmptyStage = !loading && !dashboard;
  const modeLabel = mode === 'openclaw' ? 'OpenClaw' : 'Codex';
  const trendAreaGradientId = `statistics-trend-area-gradient-${mode}-${activeRange}`;
  const [isSurfaceRefreshing, setIsSurfaceRefreshing] = useState(false);
  const [tableSort, setTableSort] = useState<TableSortState | null>(null);
  const orderedTrendPoints = useMemo(
    () =>
      [...safeDashboard.trendPoints].sort((left, right) => {
        const leftTime = Date.parse(left.timestamp);
        const rightTime = Date.parse(right.timestamp);

        if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
          return leftTime - rightTime;
        }

        return left.label.localeCompare(right.label, 'zh-CN');
      }),
    [safeDashboard.trendPoints],
  );

  const chartGeometry = useMemo(() => {
    const width = 720;
    const height = 308;
    const paddingTop = 18;
    const paddingBottom = 14;
    const observedValues = orderedTrendPoints.flatMap((point) => [point.usedTokens, point.averageTokens]);
    const observedMaxValue = Math.max(1, ...observedValues);
    const observedMinValue = observedValues.length > 0 ? Math.min(...observedValues) : 0;
    const observedRange = observedMaxValue - observedMinValue;
    const shouldRenderBackfilledPreview =
      safeDashboard.historyBackfilled &&
      orderedTrendPoints.length > 0 &&
      (orderedTrendPoints.length <= 2 || observedRange <= Math.max(observedMaxValue * 0.0015, 1));
    const previewTrendPoints = shouldRenderBackfilledPreview
      ? buildBackfilledPreviewPoints({
          range: activeRange,
          generatedAt: safeDashboard.generatedAt,
          latestPoint: orderedTrendPoints[orderedTrendPoints.length - 1],
        })
      : null;
    const domainObservedValues = previewTrendPoints
      ? [...observedValues, ...previewTrendPoints.flatMap((point) => [point.usedTokens, point.averageTokens])]
      : observedValues;
    const domainObservedMaxValue = Math.max(1, ...domainObservedValues);
    const domainObservedMinValue = domainObservedValues.length > 0 ? Math.min(...domainObservedValues) : 0;
    const domainObservedRange = domainObservedMaxValue - domainObservedMinValue;
    const domainPadding =
      domainObservedRange > 0
        ? Math.max(domainObservedRange * 0.18, domainObservedMaxValue * 0.02, 1)
        : Math.max(domainObservedMaxValue * 0.14, 1);
    const domainMinValue = Math.max(0, domainObservedMinValue - domainPadding);
    const domainMaxValue = Math.max(domainMinValue + 1, domainObservedMaxValue + domainPadding);
    const domainSpan = Math.max(1, domainMaxValue - domainMinValue);
    const innerHeight = height - paddingTop - paddingBottom;
    const { startMs, endMs } = getTrendRangeBounds(activeRange, safeDashboard.generatedAt);
    const rangeWidthMs = Math.max(1, endMs - startMs);
    const projectValueToY = (value: number): number =>
      paddingTop + innerHeight * (1 - (value - domainMinValue) / domainSpan);

    const mapPointsToCoordinates = (points: StatisticsTrendPoint[]): TrendCoordinate[] =>
      points.map((point, index, allPoints) => {
      const parsedTimestamp = Date.parse(point.timestamp);
      const fallbackTimestamp =
        startMs + (rangeWidthMs * index) / Math.max(1, allPoints.length - 1);
      const pointTimestamp = Number.isFinite(parsedTimestamp) ? parsedTimestamp : fallbackTimestamp;
      const x = clamp(((pointTimestamp - startMs) / rangeWidthMs) * width, 0, width);

      return {
        point,
        x,
        primaryY: projectValueToY(point.usedTokens),
        secondaryY: projectValueToY(point.averageTokens),
      };
    });
    const coordinates = mapPointsToCoordinates(orderedTrendPoints);
    const renderCoordinates =
      previewTrendPoints
        ? mapPointsToCoordinates(previewTrendPoints)
        : coordinates;

    const yGuides: TrendGuide[] = Array.from({ length: 5 }, (_, index) => {
      const ratio = index / 4;
      const value = Math.round(domainMaxValue - domainSpan * ratio);
      return {
        label: formatCompactValue(value),
        value,
        y: paddingTop + innerHeight * ratio,
      };
    });

    return {
      width,
      height,
      paddingTop,
      paddingBottom,
      innerHeight,
      maxValue: domainMaxValue,
      minValue: domainMinValue,
      domainSpan,
      axisBottomY: height - paddingBottom,
      coordinates,
      renderCoordinates,
      areaPath: buildAreaPath(renderCoordinates, height - paddingBottom),
      primaryPath: buildSmoothPath(renderCoordinates, 'primaryY'),
      secondaryPath: buildSmoothPath(renderCoordinates, 'secondaryY'),
      yGuides,
      axisTicks: buildTrendAxisTicks(activeRange, safeDashboard.generatedAt, width),
    };
  }, [activeRange, orderedTrendPoints, safeDashboard.generatedAt, safeDashboard.historyBackfilled]);

  const interactionCoordinates = useMemo<TrendInteractionPoint[]>(() => {
    const displayCoordinates = chartGeometry.renderCoordinates;
    if (displayCoordinates.length <= 1) {
      return displayCoordinates;
    }

    const { startMs, endMs } = getTrendRangeBounds(activeRange, safeDashboard.generatedAt);
    const rangeWidthMs = Math.max(1, endMs - startMs);
    const interactionBucketTimes = buildTrendInteractionBucketTimes(activeRange, safeDashboard.generatedAt);
    const coordinateEntries = displayCoordinates
      .map((coordinate) => ({
        ...coordinate,
        timestampMs: Date.parse(coordinate.point.timestamp),
      }))
      .filter((entry) => Number.isFinite(entry.timestampMs))
      .sort((left, right) => left.timestampMs - right.timestampMs);

    if (coordinateEntries.length <= 1) {
      return displayCoordinates;
    }

    return interactionBucketTimes.map((bucketTime) => {
      const pointDate = new Date(bucketTime);

      if (bucketTime <= coordinateEntries[0].timestampMs) {
        const firstEntry = coordinateEntries[0];
        return {
          point: {
            ...firstEntry.point,
            label: formatTrendPointLabel(pointDate, activeRange),
            shortLabel: formatTrendPointShortLabel(pointDate, activeRange),
            timestamp: pointDate.toISOString(),
          },
          x: firstEntry.x,
          primaryY: firstEntry.primaryY,
          secondaryY: firstEntry.secondaryY,
        };
      }

      if (bucketTime >= coordinateEntries[coordinateEntries.length - 1].timestampMs) {
        const lastEntry = coordinateEntries[coordinateEntries.length - 1];
        return {
          point: {
            ...lastEntry.point,
            label: formatTrendPointLabel(pointDate, activeRange),
            shortLabel: formatTrendPointShortLabel(pointDate, activeRange),
            timestamp: pointDate.toISOString(),
          },
          x: clamp(((bucketTime - startMs) / rangeWidthMs) * chartGeometry.width, 0, chartGeometry.width),
          primaryY: lastEntry.primaryY,
          secondaryY: lastEntry.secondaryY,
        };
      }

      let rightIndex = coordinateEntries.findIndex((entry) => entry.timestampMs >= bucketTime);
      if (rightIndex <= 0) {
        rightIndex = 1;
      }

      const leftEntry = coordinateEntries[rightIndex - 1];
      const rightEntry = coordinateEntries[rightIndex];
      const span = Math.max(1, rightEntry.timestampMs - leftEntry.timestampMs);
      const progress = clamp((bucketTime - leftEntry.timestampMs) / span, 0, 1);
      const interpolatedUsedTokens = Math.round(
        leftEntry.point.usedTokens + (rightEntry.point.usedTokens - leftEntry.point.usedTokens) * progress,
      );
      const interpolatedAverageTokens = Math.round(
        leftEntry.point.averageTokens +
          (rightEntry.point.averageTokens - leftEntry.point.averageTokens) * progress,
      );
      const x = clamp(((bucketTime - startMs) / rangeWidthMs) * chartGeometry.width, 0, chartGeometry.width);

      return {
        point: {
          label: formatTrendPointLabel(pointDate, activeRange),
          shortLabel: formatTrendPointShortLabel(pointDate, activeRange),
          timestamp: pointDate.toISOString(),
          usedTokens: interpolatedUsedTokens,
          averageTokens: interpolatedAverageTokens,
        },
        x,
        primaryY:
          chartGeometry.paddingTop +
          chartGeometry.innerHeight *
            (1 - (interpolatedUsedTokens - chartGeometry.minValue) / chartGeometry.domainSpan),
        secondaryY:
          chartGeometry.paddingTop +
          chartGeometry.innerHeight *
            (1 - (interpolatedAverageTokens - chartGeometry.minValue) / chartGeometry.domainSpan),
      };
    });
  }, [activeRange, chartGeometry, safeDashboard.generatedAt]);

  const [activeTrendIndex, setActiveTrendIndex] = useState<number>(Math.max(interactionCoordinates.length - 1, 0));

  useEffect(() => {
    setActiveTrendIndex(Math.max(interactionCoordinates.length - 1, 0));
  }, [activeRange, interactionCoordinates.length, safeDashboard.generatedAt]);

  useEffect(() => {
    if (!refreshSource || refreshSource === 'background') {
      setIsSurfaceRefreshing(false);
      return;
    }

    setIsSurfaceRefreshing(false);
    const frameId = window.requestAnimationFrame(() => {
      setIsSurfaceRefreshing(true);
    });
    const timerId = window.setTimeout(() => {
      setIsSurfaceRefreshing(false);
    }, loading ? 520 : 380);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timerId);
    };
  }, [activeRange, loading, refreshSource, safeDashboard.generatedAt]);

  const activeTrendCoordinate =
    interactionCoordinates[clamp(activeTrendIndex, 0, Math.max(interactionCoordinates.length - 1, 0))] ??
    null;
  const activeTrendPoint = activeTrendCoordinate?.point ?? null;
  const isLatestTrendPoint =
    Boolean(activeTrendCoordinate) &&
    activeTrendIndex === Math.max(interactionCoordinates.length - 1, 0);
  const activeTrendTitle = activeTrendPoint?.label ?? '';
  const trendTooltipAlignment =
    !activeTrendCoordinate || activeTrendCoordinate.x < 118
      ? 'is-left'
      : activeTrendCoordinate.x > chartGeometry.width - 118
        ? 'is-right'
        : 'is-center';
  const sortedTableRows = useMemo(() => {
    if (!tableSort) {
      return safeDashboard.tableRows;
    }

    return safeDashboard.tableRows
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const baseDelta = compareStatisticsTableRows(left.row, right.row, tableSort);
        const directionalDelta = tableSort.direction === 'asc' ? baseDelta : -baseDelta;

        if (directionalDelta !== 0) {
          return directionalDelta;
        }

        return left.index - right.index;
      })
      .map((entry) => entry.row);
  }, [safeDashboard.tableRows, tableSort]);

  const handleTableSort = (key: TableSortKey): void => {
    setTableSort((currentSort) =>
      currentSort?.key === key
        ? {
            key,
            direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
          }
        : {
            key,
            direction: getInitialTableSortDirection(key),
          },
    );
  };

  const handleTrendPointerMove = (
    event: ReactPointerEvent<SVGRectElement>,
  ): void => {
    if (interactionCoordinates.length === 0) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeX = ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * chartGeometry.width;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    interactionCoordinates.forEach((coordinate, index) => {
      const distance = Math.abs(coordinate.x - relativeX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setActiveTrendIndex(nearestIndex);
  };

  const handleExport = (): void => {
    if (!dashboard) {
      return;
    }

    const blob = new Blob(
      [`\uFEFF${buildStatisticsExportDocument(dashboard, mode, profileMetaById, contextMeta)}`],
      {
      type: 'application/vnd.ms-excel;charset=utf-8',
      },
    );
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `statistics-center-${mode}-${dashboard.generatedAt.replace(/[:.]/g, '-')}.xls`;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  };

  const healthTotal = safeDashboard.healthDistribution.reduce((sum, item) => sum + item.count, 0);
  const tokenTotal = safeDashboard.tokenDistribution.reduce((sum, item) => sum + item.value, 0);

  return (
    <section className={`statistics-center${isSurfaceRefreshing ? ' is-refreshing' : ''}`}>
      <div className="statistics-center__background" aria-hidden="true" />

      <header className="statistics-center__header">
        <div className="statistics-center__hero">
          <div className="statistics-center__title-row">
            <button
              type="button"
              className="statistics-center__brand-button"
              onClick={onClose}
              aria-label="返回空间卡片"
              title="返回空间卡片"
            >
              <img src={OAUTH_PROVIDER_ICON_SRC.codex} alt="" />
            </button>
            <div className="statistics-center__heading">
              <h2>{modeLabel}统计中心</h2>
            </div>
          </div>
          <div className="statistics-center__mode-tabs" aria-label="统计模式">
            <button
              type="button"
              className={mode === 'codex' ? 'is-active' : ''}
              onClick={() => onModeChange('codex')}
            >
              <Grid size={14} strokeWidth={2} />
              Codex
            </button>
            <button
              type="button"
              className={mode === 'openclaw' ? 'is-active' : ''}
              onClick={() => onModeChange('openclaw')}
            >
              <Box size={14} strokeWidth={2} />
              OpenClaw
            </button>
          </div>
        </div>

        <div className="statistics-center__toolbar">
          <div className="statistics-center__range-tabs" role="tablist" aria-label="时间范围">
            {(Object.keys(RANGE_LABELS) as StatisticsRange[]).map((range) => (
              <button
                key={range}
                type="button"
                className={range === activeRange ? 'is-active' : ''}
                onClick={() => onRangeChange(range)}
              >
                {RANGE_LABELS[range]}
              </button>
            ))}
          </div>

          <button type="button" className="statistics-center__toolbar-button" onClick={onRefresh}>
            {loading ? (
              <LoaderCircle size={15} strokeWidth={1.8} className="spin" />
            ) : (
              <RefreshCw size={15} strokeWidth={1.8} />
            )}
            刷新
          </button>
          <button
            type="button"
            className="statistics-center__toolbar-button statistics-center__toolbar-button--secondary"
            onClick={handleExport}
            disabled={!dashboard}
          >
            <Download size={15} strokeWidth={1.8} />
            导出
          </button>
        </div>
      </header>

      {errorMessage ? <div className="statistics-center__error">{errorMessage}</div> : null}
      {showLoadingStage ? (
        <div className="statistics-center__status-banner" role="status" aria-live="polite">
          <LoaderCircle size={16} strokeWidth={1.9} className="spin" />
          <span>正在整理统计数据，面板会优先显示当前已识别到的真实状态。</span>
        </div>
      ) : null}
      {showEmptyStage && !showLoadingStage ? (
        <div className="statistics-center__status-banner statistics-center__status-banner--warning">
          <span>统计服务暂未返回完整数据，当前先保留面板结构，你可以点右上角刷新重试。</span>
        </div>
      ) : null}

      <div className="statistics-center__kpi-grid">
        <article className="statistics-kpi-card statistics-kpi-card--blue">
          <div>
            <span className="statistics-kpi-card__label">总空间数</span>
            <strong>{safeDashboard.totalSpaces}</strong>
            <div className="statistics-kpi-card__delta is-positive">
              {safeDashboard.compareLabel} <span>{formatSignedDelta(safeDashboard.totalSpacesDeltaPercent)}</span>
            </div>
          </div>
          <div className="statistics-kpi-card__icon">
            <StatisticsMetricIcon kind="spaces" />
          </div>
          <div className="statistics-kpi-card__wave" aria-hidden="true" />
        </article>

        <article className="statistics-kpi-card statistics-kpi-card--green">
          <div>
            <span className="statistics-kpi-card__label">可用空间</span>
            <strong>{safeDashboard.availableSpaces}</strong>
            <div
              className={`statistics-kpi-card__delta ${safeDashboard.availableSpacesDeltaPercent >= 0 ? 'is-positive' : 'is-negative'}`}
            >
              {safeDashboard.compareLabel} <span>{formatSignedDelta(safeDashboard.availableSpacesDeltaPercent)}</span>
            </div>
          </div>
          <div className="statistics-kpi-card__icon">
            <StatisticsMetricIcon kind="available" />
          </div>
          <div className="statistics-kpi-card__wave" aria-hidden="true" />
        </article>

        <article className="statistics-kpi-card statistics-kpi-card--violet">
          <div>
            <span className="statistics-kpi-card__label">周消耗</span>
            <strong>{formatLargeInteger(safeDashboard.estimatedUsedTokens)}</strong>
            <div
              className={`statistics-kpi-card__delta ${safeDashboard.usedTokensDeltaPercent >= 0 ? 'is-positive' : 'is-negative'}`}
            >
              {safeDashboard.compareLabel} <span>{formatSignedDelta(safeDashboard.usedTokensDeltaPercent)}</span>
            </div>
          </div>
          <div className="statistics-kpi-card__icon">
            <StatisticsMetricIcon kind="used" />
          </div>
          <div className="statistics-kpi-card__wave" aria-hidden="true" />
        </article>

        <article className="statistics-kpi-card statistics-kpi-card--cyan">
          <div>
            <span className="statistics-kpi-card__label">估算剩余</span>
            <strong>{formatLargeInteger(safeDashboard.estimatedRemainingTokens)}</strong>
            <div
              className={`statistics-kpi-card__delta ${safeDashboard.remainingTokensDeltaPercent >= 0 ? 'is-positive' : 'is-negative'}`}
            >
              {safeDashboard.compareLabel} <span>{formatSignedDelta(safeDashboard.remainingTokensDeltaPercent)}</span>
            </div>
          </div>
          <div className="statistics-kpi-card__icon">
            <StatisticsMetricIcon kind="remaining" />
          </div>
          <div className="statistics-kpi-card__wave" aria-hidden="true" />
        </article>

        <article className="statistics-kpi-card statistics-kpi-card--emerald">
          <div>
            <span className="statistics-kpi-card__label">健康度</span>
            <strong>{formatPercent(safeDashboard.averageHealthPercent)}</strong>
            <div
              className={`statistics-kpi-card__delta ${safeDashboard.averageHealthDeltaPercent >= 0 ? 'is-positive' : 'is-negative'}`}
            >
              {safeDashboard.compareLabel} <span>{formatSignedDelta(safeDashboard.averageHealthDeltaPercent)}</span>
            </div>
          </div>
          <div className="statistics-kpi-card__icon">
            <StatisticsMetricIcon kind="health" />
          </div>
          <div className="statistics-kpi-card__progress">
            <span style={{ width: `${clamp(safeDashboard.averageHealthPercent, 0, 100)}%` }} />
          </div>
        </article>

        <article className="statistics-kpi-card statistics-kpi-card--purple">
          <div>
            <span className="statistics-kpi-card__label">估算总容量</span>
            <strong>{formatCompactValue(safeDashboard.estimatedTotalTokens)}</strong>
            <div
              className={`statistics-kpi-card__delta ${safeDashboard.totalTokensDeltaPercent >= 0 ? 'is-positive' : 'is-negative'}`}
            >
              {safeDashboard.compareLabel} <span>{formatSignedDelta(safeDashboard.totalTokensDeltaPercent)}</span>
            </div>
          </div>
          <div className="statistics-kpi-card__icon">
            <StatisticsMetricIcon kind="token" />
          </div>
          <div className="statistics-kpi-card__wave" aria-hidden="true" />
        </article>
      </div>

      <div className="statistics-center__content-grid">
        <article className="statistics-panel statistics-panel--trend">
          <div className="statistics-panel__header">
            <div>
              <h3>额度消耗趋势</h3>
              <div className="statistics-panel__legend">
                <span className="is-primary">消耗 (Token)</span>
                <span className="is-secondary">7日均值</span>
              </div>
              {safeDashboard.historyBackfilled ? (
                <div className="statistics-panel__hint">历史样本还在积累中，曲线会继续按真实采样补全。</div>
              ) : null}
            </div>
            <button type="button" className="statistics-panel__select">
              Token
            </button>
          </div>

          <div className="statistics-trend-chart">
            <div className="statistics-trend-chart__grid" aria-hidden="true">
              {chartGeometry.yGuides.map((guide) => (
                <span key={guide.label} style={{ top: `${(guide.y / chartGeometry.height) * 100}%` }} />
              ))}
            </div>
            <div className="statistics-trend-chart__y-axis" aria-hidden="true">
              {chartGeometry.yGuides.map((guide) => (
                <span key={guide.label} style={{ top: `${(guide.y / chartGeometry.height) * 100}%` }}>
                  {guide.label}
                </span>
              ))}
            </div>
            <svg
              className="statistics-trend-chart__svg"
              viewBox={`0 0 ${chartGeometry.width} ${chartGeometry.height}`}
              preserveAspectRatio="none"
              aria-label="额度消耗趋势图"
            >
              <defs>
                <linearGradient id={trendAreaGradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="4%" stopColor="#4f7bff" stopOpacity="0.34" />
                  <stop offset="68%" stopColor="#4f7bff" stopOpacity="0.12" />
                  <stop offset="100%" stopColor="#4f7bff" stopOpacity="0" />
                </linearGradient>
              </defs>
              <line
                className="statistics-trend-chart__axis-line statistics-trend-chart__axis-line--y"
                x1="0"
                x2="0"
                y1={chartGeometry.paddingTop}
                y2={chartGeometry.axisBottomY}
              />
              <line
                className="statistics-trend-chart__axis-line statistics-trend-chart__axis-line--x"
                x1="0"
                x2={chartGeometry.width}
                y1={chartGeometry.axisBottomY}
                y2={chartGeometry.axisBottomY}
              />
              {chartGeometry.areaPath ? (
                <path
                  className="statistics-trend-chart__area"
                  d={chartGeometry.areaPath}
                  fill={`url(#${trendAreaGradientId})`}
                />
              ) : null}
              {chartGeometry.secondaryPath ? (
                <path
                  className="statistics-trend-chart__line statistics-trend-chart__line--secondary"
                  d={chartGeometry.secondaryPath}
                />
              ) : null}
              {chartGeometry.primaryPath ? (
                <path
                  className="statistics-trend-chart__line statistics-trend-chart__line--primary"
                  d={chartGeometry.primaryPath}
                />
              ) : null}
              {chartGeometry.coordinates.map((coordinate) => (
                <g key={`${coordinate.point.timestamp}-${coordinate.point.usedTokens}-${coordinate.point.averageTokens}`}>
                  <circle
                    className="statistics-trend-chart__point statistics-trend-chart__point--secondary"
                    cx={coordinate.x}
                    cy={coordinate.secondaryY}
                    r="3.6"
                  />
                  <circle
                    className="statistics-trend-chart__point statistics-trend-chart__point--primary"
                    cx={coordinate.x}
                    cy={coordinate.primaryY}
                    r="4.6"
                  />
                </g>
              ))}
              {activeTrendCoordinate ? (
                <>
                  <line
                    className="statistics-trend-chart__guide-line"
                    x1={activeTrendCoordinate.x}
                    x2={activeTrendCoordinate.x}
                    y1={chartGeometry.paddingTop - 4}
                    y2={chartGeometry.height - 2}
                  />
                  <circle
                    className="statistics-trend-chart__point statistics-trend-chart__point--secondary"
                    cx={activeTrendCoordinate.x}
                    cy={activeTrendCoordinate.secondaryY}
                    r="4.6"
                  />
                  <circle
                    className="statistics-trend-chart__point statistics-trend-chart__point--primary"
                    cx={activeTrendCoordinate.x}
                    cy={activeTrendCoordinate.primaryY}
                    r="5.6"
                  />
                </>
              ) : null}
              <rect
                className="statistics-trend-chart__interaction-layer"
                x="0"
                y="0"
                width={chartGeometry.width}
                height={chartGeometry.height}
                fill="transparent"
                onPointerMove={handleTrendPointerMove}
              />
            </svg>
            {activeTrendPoint && activeTrendCoordinate ? (
              <div
                className={`statistics-trend-chart__callout ${trendTooltipAlignment}`}
                style={{ left: `${(activeTrendCoordinate.x / chartGeometry.width) * 100}%` }}
              >
                <div className="statistics-trend-chart__callout-time">{activeTrendTitle}</div>
                <div className="statistics-trend-chart__callout-row">
                  <span className="statistics-trend-chart__callout-dot is-primary" aria-hidden="true" />
                  <span>消耗 (Token)：{formatLargeInteger(activeTrendPoint.usedTokens)}</span>
                </div>
                <div className="statistics-trend-chart__callout-row">
                  <span className="statistics-trend-chart__callout-dot is-secondary" aria-hidden="true" />
                  <span>7日均值：{formatLargeInteger(activeTrendPoint.averageTokens)}</span>
                </div>
              </div>
            ) : null}
            <div className={`statistics-trend-chart__labels statistics-trend-chart__labels--${activeRange}`} aria-hidden="true">
              {chartGeometry.axisTicks.map((tick) => (
                <span
                  key={`${activeRange}-${tick.label}-${tick.x}`}
                  className={`statistics-trend-chart__label is-anchor-${tick.anchor}`}
                  style={{
                    left: `${(tick.x / chartGeometry.width) * 100}%`,
                    bottom: tick.row === 0 ? '0px' : '16px',
                  }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>
        </article>

        <article className="statistics-panel statistics-panel--ranking">
          <div className="statistics-panel__header">
            <h3>周消耗排行 (TOP 8)</h3>
            <button type="button" className="statistics-panel__text-action">
              更多
            </button>
          </div>
          <div className="statistics-ranking">
            {safeDashboard.ranking.map((item) => (
              <div key={`${safeDashboard.generatedAt}-${item.profileId}`} className="statistics-ranking__row">
                <span className="statistics-ranking__label">{item.name}</span>
                <div className="statistics-ranking__bar">
                  <span
                    className={`statistics-ranking__fill statistics-ranking__fill--${item.accent}`}
                    style={{ width: `${clamp(item.sharePercent, 0, 100)}%` }}
                  />
                </div>
                <div className="statistics-ranking__value">
                  <strong>{formatLargeInteger(item.usedTokens)}</strong>
                  <span>({formatSharePercent(item.sharePercent)})</span>
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="statistics-panel statistics-panel--compact statistics-panel--health">
          <div className="statistics-panel__header">
            <h3>空间状态分布</h3>
            <button type="button" className="statistics-panel__text-action">
              更多
            </button>
          </div>
          <div className="statistics-side-breakdown">
            <CircularBreakdownChart
              total={healthTotal}
              items={safeDashboard.healthDistribution}
              centerLabel="总空间"
              centerValue={String(safeDashboard.totalSpaces)}
              tone="health"
            />
            <div className="statistics-breakdown-list">
              {safeDashboard.healthDistribution.map((item) => (
                <div key={item.label} className="statistics-breakdown-list__row">
                  <span className={`statistics-breakdown-list__dot statistics-breakdown-list__dot--${item.tone}`} />
                  <span>{item.label}</span>
                  <strong>{item.count}</strong>
                  <span>{formatSharePercent(item.sharePercent)}</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="statistics-panel statistics-panel--table">
          <div className="statistics-panel__header">
            <h3>空间使用明细</h3>
            <div className="statistics-panel__meta">共 {safeDashboard.tableRows.length} 条</div>
          </div>
          <div className="statistics-table">
            <div className="statistics-table__head">
              <button
                type="button"
                className={`statistics-table__head-button${tableSort?.key === 'name' ? ' is-active' : ''}`}
                aria-pressed={tableSort?.key === 'name'}
                onClick={() => handleTableSort('name')}
              >
                空间名称
              </button>
              <button
                type="button"
                className={`statistics-table__head-button is-center${tableSort?.key === 'planType' ? ' is-active' : ''}`}
                aria-pressed={tableSort?.key === 'planType'}
                onClick={() => handleTableSort('planType')}
              >
                类型
              </button>
              <button
                type="button"
                className={`statistics-table__head-button is-center${tableSort?.key === 'status' ? ' is-active' : ''}`}
                aria-pressed={tableSort?.key === 'status'}
                onClick={() => handleTableSort('status')}
              >
                状态
              </button>
              <button
                type="button"
                className={`statistics-table__head-button is-center${tableSort?.key === 'usedTokens' ? ' is-active' : ''}`}
                aria-pressed={tableSort?.key === 'usedTokens'}
                onClick={() => handleTableSort('usedTokens')}
              >
                本周已用 (Token)
              </button>
              <button
                type="button"
                className={`statistics-table__head-button is-center${tableSort?.key === 'remainingTokens' ? ' is-active' : ''}`}
                aria-pressed={tableSort?.key === 'remainingTokens'}
                onClick={() => handleTableSort('remainingTokens')}
              >
                估算剩余 (Token)
              </button>
              <button
                type="button"
                className={`statistics-table__head-button is-center${tableSort?.key === 'usagePercent' ? ' is-active' : ''}`}
                aria-pressed={tableSort?.key === 'usagePercent'}
                onClick={() => handleTableSort('usagePercent')}
              >
                使用率
              </button>
              <button
                type="button"
                className={`statistics-table__head-button is-center${tableSort?.key === 'lastSyncAt' ? ' is-active' : ''}`}
                aria-pressed={tableSort?.key === 'lastSyncAt'}
                onClick={() => handleTableSort('lastSyncAt')}
              >
                最后同步
              </button>
              <span className="is-center">建议操作</span>
            </div>
            <div className="statistics-table__body">
              {sortedTableRows.map((row) => (
                <div key={row.profileId} className="statistics-table__row">
                  <span className="statistics-table__name-stack">
                    <strong className="statistics-table__name">{row.name}</strong>
                    <em className="statistics-table__account">{getProfileAccountId(profileMetaById, row.profileId)}</em>
                  </span>
                  <span className="statistics-table__cell statistics-table__cell--center">
                    <span className={`statistics-table__pill statistics-table__pill--${row.planType.toLowerCase()}`}>
                      {row.planType}
                    </span>
                  </span>
                  <span className="statistics-table__cell statistics-table__cell--center">
                    <span className={`statistics-table__status statistics-table__status--${row.statusTone}`}>
                      {row.statusLabel}
                    </span>
                  </span>
                  <span className="statistics-table__cell statistics-table__cell--center">
                    {formatLargeInteger(row.usedTokens)}
                  </span>
                  <span className="statistics-table__cell statistics-table__cell--center">
                    {formatLargeInteger(row.remainingTokens)}
                  </span>
                  <span className="statistics-table__usage-cell">
                    <span className="statistics-table__usage-track">
                      <span
                        className={`statistics-table__usage-fill statistics-table__usage-fill--${row.statusTone}`}
                        style={{ width: `${clamp(row.usagePercent, 0, 100)}%` }}
                      />
                    </span>
                    <em>{formatPercent(row.usagePercent)}</em>
                  </span>
                  <span className="statistics-table__cell statistics-table__cell--center">
                    {row.lastSyncAt
                      ? new Date(row.lastSyncAt).toLocaleString('zh-CN', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })
                      : '--'}
                  </span>
                  <span className="statistics-table__cell statistics-table__cell--center">
                    <button type="button" className="statistics-table__action">
                      {row.actionLabel}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="statistics-panel statistics-panel--compact statistics-panel--token">
          <div className="statistics-panel__header">
            <h3>Token 估算 (周)</h3>
            <button type="button" className="statistics-panel__text-action">
              更多
            </button>
          </div>
          <div className="statistics-side-breakdown">
            <CircularBreakdownChart
              total={tokenTotal}
              items={safeDashboard.tokenDistribution}
              centerLabel="总量"
              centerValue={formatCompactValue(safeDashboard.estimatedTotalTokens)}
              tone="token"
            />
            <div className="statistics-breakdown-list">
              {safeDashboard.tokenDistribution.map((item) => (
                <div key={item.label} className="statistics-breakdown-list__row">
                  <span className={`statistics-breakdown-list__dot statistics-breakdown-list__dot--${item.tone}`} />
                  <span>{item.label}</span>
                  <strong>{formatCompactValue(item.value)}</strong>
                  <span>{formatSharePercent(item.sharePercent)}</span>
                </div>
              ))}
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
